import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { OptionalProperty } from '@/utils/utilityTypes';

import type { Message } from './types';

type CopilotState = {
  sessionId: string | null;
  messages: Message[];
  actions: {
    startNewSession: () => void;
    addUserMessage: (message: MessagePayload) => void;
    startAssistantMessage: (
      message: Omit<AssistantMessagePayload, 'status'>,
    ) => void;
    finishAssistantMessage: (message: AssistantMessagePayload) => void;
    setSessionId: (sessionId: string | null) => void;
    removeLastMessage: () => void;
  };
};

type MessagePayload = {
  id: string;
  content: string;
};

type AssistantMessagePayload = OptionalProperty<MessagePayload, 'content'> & {
  status: 'success' | 'error';
};

const useCopilotStore = create<CopilotState>()(
  persist(
    (set) => ({
      sessionId: null,
      messages: [],
      actions: {
        startNewSession: () => set({ sessionId: null, messages: [] }),
        setSessionId: (sessionId: string | null) => set({ sessionId }),
        addUserMessage: ({ id, content }: MessagePayload) =>
          set((state) => ({
            messages: [
              ...state.messages,
              { id, content, role: 'user', status: 'success' },
            ],
          })),
        startAssistantMessage: ({
          id,
        }: Omit<AssistantMessagePayload, 'status'>) =>
          set((state) => ({
            messages: [
              ...state.messages,
              {
                id,
                role: 'assistant',
                status: 'loading',
              },
            ],
          })),
        finishAssistantMessage: ({
          id,
          content,
          status,
        }: AssistantMessagePayload) =>
          set((state) => {
            const message = state.messages.find((message) => message.id === id);
            if (!message) {
              return { messages: state.messages };
            }

            return {
              messages: state.messages.map((message) =>
                message.id === id ? { ...message, content, status } : message,
              ),
            };
          }),
        removeLastMessage: () =>
          set((state) => ({
            messages: state.messages.slice(0, -1),
          })),
      },
    }),
    {
      name: 'copilot',
      partialize: (state) => ({
        sessionId: state.sessionId,
        messages: state.messages,
      }),
    },
  ),
);

export const useSessionId = () => useCopilotStore((state) => state.sessionId);
export const useMessages = () => useCopilotStore((state) => state.messages);
export const useCopilotActions = () =>
  useCopilotStore((state) => state.actions);
